import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { call } from "../../../firebase";
import {
  deleteDeckCard,
  deleteDeckCascade,
  getDeck,
  getDeckSummary,
  getSrsStreak,
  importCards,
  listDeckCards,
  updateDeckCardMedia,
} from "../firestore";
import {
  deleteStoragePath,
  uploadCardAudio,
  uploadCardImage,
  uploadGeneratedCardAudioB64,
  uploadGeneratedCardImageB64,
} from "../storage";
import StreakBanner from "../components/StreakBanner";

export default function DeckDetailPage() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const [deck, setDeck] = useState(null);
  const [summary, setSummary] = useState({ dueCount: 0, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [moreInstruction, setMoreInstruction] = useState("");
  const [moreCount, setMoreCount] = useState(10);
  const [generatingMore, setGeneratingMore] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const [generateSuccess, setGenerateSuccess] = useState("");
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [cardsError, setCardsError] = useState("");
  const [deletingCardId, setDeletingCardId] = useState("");
  const [mediaBusyId, setMediaBusyId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [dueOnly, setDueOnly] = useState(false);

  const [audioDialogOpen, setAudioDialogOpen] = useState(false);
  const [audioDialogCard, setAudioDialogCard] = useState(null);
  const [audioText, setAudioText] = useState("");
  const [audioInstruction, setAudioInstruction] = useState("");
  const [audioVoice, setAudioVoice] = useState("alloy");
  const [streak, setStreak] = useState({ streakCount: 0, lastStudyDate: null });

  const loadDeckData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [deckData, summaryData] = await Promise.all([
        getDeck(deckId),
        getDeckSummary(deckId),
      ]);
      setDeck(deckData);
      setSummary(summaryData);
      const streakData = await getSrsStreak();
      setStreak(streakData);
    } catch (err) {
      setError(err.message || "Failed to load deck.");
    } finally {
      setLoading(false);
    }
  }, [deckId]);

  const loadCards = useCallback(async () => {
    setCardsLoading(true);
    setCardsError("");
    try {
      const data = await listDeckCards(deckId, 800);
      setCards(data);
    } catch (err) {
      setCardsError(err.message || "Failed to load cards.");
    } finally {
      setCardsLoading(false);
    }
  }, [deckId]);

  useEffect(() => {
    loadDeckData();
    loadCards();
  }, [loadDeckData, loadCards]);

  const handleDeleteDeck = async () => {
    if (!deck) return;
    if (!window.confirm(`Delete deck "${deck.name}" and all its cards, sessions, and reviews?`)) return;
    setDeleting(true);
    setError("");
    try {
      await deleteDeckCascade(deckId);
      navigate("/srs");
    } catch (err) {
      setError(err.message || "Failed to delete deck.");
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateMore = async () => {
    if (!deck) return;
    setGenerateError("");
    setGenerateSuccess("");
    setGeneratingMore(true);
    try {
      const res = await call("generateMoreSrsCards", {
        deckId,
        instruction: moreInstruction.trim(),
        targetCount: Number(moreCount) || 10,
      });
      const nextCards = res?.data?.cards || [];
      if (!nextCards.length) throw new Error("No cards returned.");
      await importCards(deckId, nextCards);
      await Promise.all([loadDeckData(), loadCards()]);
      setGenerateSuccess(`Added ${nextCards.length} cards.`);
    } catch (err) {
      const msg = err?.code === "functions/not-found"
        ? "generateMoreSrsCards is not deployed. Deploy Firebase functions."
        : err.message || "Failed to generate additional cards.";
      setGenerateError(msg);
    } finally {
      setGeneratingMore(false);
    }
  };

  const handleDeleteCard = async (card) => {
    if (!window.confirm(`Delete this card?\n\nFront: ${card.front}`)) return;
    setDeletingCardId(card.id);
    setCardsError("");
    try {
      await deleteDeckCard(card.id);
      await Promise.all([loadCards(), loadDeckData()]);
    } catch (err) {
      setCardsError(err.message || "Failed to delete card.");
    } finally {
      setDeletingCardId("");
    }
  };

  const handleUploadImage = async (card, file) => {
    if (!file) return;
    setMediaBusyId(card.id);
    setCardsError("");
    try {
      const uploaded = await uploadCardImage({ deckId, cardId: card.id, file });
      await updateDeckCardMedia(card.id, uploaded);
      await loadCards();
    } catch (err) {
      setCardsError(err.message || "Failed to upload image.");
    } finally {
      setMediaBusyId("");
    }
  };

  const handleUploadAudio = async (card, file) => {
    if (!file) return;
    setMediaBusyId(card.id);
    setCardsError("");
    try {
      const uploaded = await uploadCardAudio({ deckId, cardId: card.id, file });
      await updateDeckCardMedia(card.id, uploaded);
      await loadCards();
    } catch (err) {
      setCardsError(err.message || "Failed to upload audio.");
    } finally {
      setMediaBusyId("");
    }
  };

  const handleRemoveImage = async (card) => {
    setMediaBusyId(card.id);
    setCardsError("");
    try {
      await deleteStoragePath(card.imagePath);
      await updateDeckCardMedia(card.id, { imageUrl: null, imagePath: null });
      await loadCards();
    } catch (err) {
      setCardsError(err.message || "Failed to remove image.");
    } finally {
      setMediaBusyId("");
    }
  };

  const handleRemoveAudio = async (card) => {
    setMediaBusyId(card.id);
    setCardsError("");
    try {
      await deleteStoragePath(card.audioPath);
      await updateDeckCardMedia(card.id, { audioUrl: null, audioPath: null });
      await loadCards();
    } catch (err) {
      setCardsError(err.message || "Failed to remove audio.");
    } finally {
      setMediaBusyId("");
    }
  };

  const handleGenerateImage = async (card) => {
    const instruction = window.prompt("Optional image instruction for GPT (leave blank for default):", "");
    if (instruction === null) return;
    setMediaBusyId(card.id);
    setCardsError("");
    try {
      const res = await call("generateSrsCardImage", {
        cardId: card.id,
        instruction: instruction.trim(),
      });
      const b64 = res?.data?.b64;
      if (!b64) throw new Error("No generated image returned.");
      const uploaded = await uploadGeneratedCardImageB64({ deckId, cardId: card.id, b64 });
      await updateDeckCardMedia(card.id, uploaded);
      await loadCards();
    } catch (err) {
      const msg = err?.code === "functions/not-found"
        ? "generateSrsCardImage is not deployed. Deploy Firebase functions."
        : err.message || "Failed to generate image.";
      setCardsError(msg);
    } finally {
      setMediaBusyId("");
    }
  };

  const openAudioDialog = (card) => {
    setAudioDialogCard(card);
    setAudioText(String(card.back || card.front || ""));
    setAudioInstruction("");
    setAudioVoice("alloy");
    setAudioDialogOpen(true);
  };

  const closeAudioDialog = () => {
    setAudioDialogOpen(false);
    setAudioDialogCard(null);
  };

  const submitGenerateAudio = async () => {
    if (!audioDialogCard) return;
    if (!audioText.trim()) {
      setCardsError("Audio text cannot be empty.");
      return;
    }
    const card = audioDialogCard;
    setMediaBusyId(card.id);
    setCardsError("");
    try {
      const res = await call("generateSrsCardAudio", {
        cardId: card.id,
        text: audioText.trim(),
        instruction: audioInstruction.trim(),
        voice: audioVoice,
      });
      const b64 = res?.data?.b64;
      if (!b64) throw new Error("No generated audio returned.");
      const uploaded = await uploadGeneratedCardAudioB64({ deckId, cardId: card.id, b64 });
      await updateDeckCardMedia(card.id, uploaded);
      await loadCards();
      closeAudioDialog();
    } catch (err) {
      const msg = err?.code === "functions/not-found"
        ? "generateSrsCardAudio is not deployed. Deploy Firebase functions."
        : err.message || "Failed to generate audio.";
      setCardsError(msg);
    } finally {
      setMediaBusyId("");
    }
  };

  const filteredCards = useMemo(() => {
    const now = new Date();
    const search = searchText.trim().toLowerCase();
    const tag = tagFilter.trim().toLowerCase();

    const isDue = (card) => {
      const dueAt = card?.srs?.dueAt;
      const dueDate = dueAt?.toDate ? dueAt.toDate() : (dueAt ? new Date(dueAt) : null);
      if (!dueDate || Number.isNaN(dueDate.getTime())) return false;
      return dueDate <= now;
    };

    return cards.filter((card) => {
      if (search) {
        const haystack = `${card.front || ""} ${card.back || ""}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (tag) {
        const hasTag = (card.tags || []).some((t) => String(t).toLowerCase().includes(tag));
        if (!hasTag) return false;
      }
      if (dueOnly && !isDue(card)) return false;
      return true;
    });
  }, [cards, dueOnly, searchText, tagFilter]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Button component={Link} to="/srs" sx={{ alignSelf: "flex-start" }}>Back to Decks</Button>
        <StreakBanner streakCount={streak.streakCount} lastStudyDate={streak.lastStudyDate} />

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Typography>Loading deck...</Typography>
        ) : deck ? (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h4" fontWeight={800}>{deck.name}</Typography>
                <Typography color="text.secondary">{deck.description || "No description"}</Typography>
              </Box>

              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{summary.totalCount}</Typography>
                  <Typography color="text.secondary">Total Cards</Typography>
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{summary.dueCount}</Typography>
                  <Typography color="text.secondary">Due Now</Typography>
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button component={Link} to={`/srs/decks/${deckId}/study`} variant="contained">Study</Button>
                <Button component={Link} to={`/srs/decks/${deckId}/import`} variant="outlined">Import Deck</Button>
                <Button color="error" variant="outlined" onClick={handleDeleteDeck} disabled={deleting}>
                  {deleting ? "Deleting..." : "Delete Deck"}
                </Button>
              </Stack>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="h6" fontWeight={700}>Generate More Cards</Typography>
                  <TextField
                    label="Instruction (optional)"
                    placeholder="e.g., add more progressive integral cards with formal definitions in LaTeX"
                    value={moreInstruction}
                    onChange={(e) => setMoreInstruction(e.target.value)}
                    multiline
                    minRows={2}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      label="Cards"
                      type="number"
                      value={moreCount}
                      onChange={(e) => setMoreCount(e.target.value)}
                      inputProps={{ min: 1, max: 100 }}
                      sx={{ width: 140 }}
                    />
                    <Button variant="outlined" onClick={handleGenerateMore} disabled={generatingMore}>
                      {generatingMore ? "Generating..." : "Generate More Cards"}
                    </Button>
                  </Stack>
                  {generateSuccess ? <Alert severity="success">{generateSuccess}</Alert> : null}
                  {generateError ? <Alert severity="error">{generateError}</Alert> : null}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="h6" fontWeight={700}>Cards</Typography>
                  {cardsError ? <Alert severity="error">{cardsError}</Alert> : null}
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                      size="small"
                      label="Search front/back"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      sx={{ minWidth: 220 }}
                    />
                    <TextField
                      size="small"
                      label="Filter tag"
                      value={tagFilter}
                      onChange={(e) => setTagFilter(e.target.value)}
                      sx={{ minWidth: 180 }}
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">Due only</Typography>
                      <Switch checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                      Showing {filteredCards.length} of {cards.length}
                    </Typography>
                  </Stack>
                  {cardsLoading ? (
                    <Typography color="text.secondary">Loading cards...</Typography>
                  ) : filteredCards.length === 0 ? (
                    <Typography color="text.secondary">No cards yet.</Typography>
                  ) : (
                    <Box sx={{ maxHeight: 380, overflow: "auto" }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "30%" }}>Front</TableCell>
                            <TableCell sx={{ width: "35%" }}>Back</TableCell>
                            <TableCell sx={{ width: "18%" }}>Tags</TableCell>
                            <TableCell sx={{ width: "22%" }}>Media</TableCell>
                            <TableCell sx={{ width: "10%" }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredCards.map((card) => (
                            <TableRow key={card.id} hover>
                              <TableCell>{card.front}</TableCell>
                              <TableCell>{card.back}</TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {(card.tags || []).join(", ") || "None"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Stack spacing={1}>
                                  <Typography variant="caption" color="text.secondary">{card.imageUrl ? "Image attached" : "No image"}</Typography>
                                  {card.imageUrl ? (
                                    <Box component="img" src={card.imageUrl} alt="card visual" sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: 1, border: "1px solid #ddd" }} />
                                  ) : null}
                                  <Typography variant="caption" color="text.secondary">{card.audioUrl ? "Audio attached" : "No audio"}</Typography>
                                  {card.audioUrl ? <audio controls preload="none" src={card.audioUrl} style={{ width: 170 }} /> : null}
                                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    <Button size="small" component="label" variant="outlined" disabled={mediaBusyId === card.id}>
                                      Img
                                      <input hidden type="file" accept="image/*" onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        e.target.value = "";
                                        handleUploadImage(card, file);
                                      }} />
                                    </Button>
                                    <Button size="small" component="label" variant="outlined" disabled={mediaBusyId === card.id}>
                                      Audio
                                      <input hidden type="file" accept="audio/*,.mp3,.m4a,.wav,.ogg" onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        e.target.value = "";
                                        handleUploadAudio(card, file);
                                      }} />
                                    </Button>
                                    <Button size="small" variant="outlined" disabled={mediaBusyId === card.id} onClick={() => handleGenerateImage(card)}>GPT Img</Button>
                                    <Button size="small" variant="outlined" disabled={mediaBusyId === card.id} onClick={() => openAudioDialog(card)}>GPT Aud</Button>
                                    {card.imageUrl ? <Button size="small" color="error" variant="text" disabled={mediaBusyId === card.id} onClick={() => handleRemoveImage(card)}>rm img</Button> : null}
                                    {card.audioUrl ? <Button size="small" color="error" variant="text" disabled={mediaBusyId === card.id} onClick={() => handleRemoveAudio(card)}>rm aud</Button> : null}
                                  </Stack>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1}>
                                  <Button size="small" variant="outlined" component={Link} to={`/srs/decks/${deckId}/cards/${card.id}`}>Edit</Button>
                                  <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteCard(card)} disabled={deletingCardId === card.id}>
                                    {deletingCardId === card.id ? "Deleting..." : "Delete"}
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Paper>
        ) : null}
      </Stack>

      <Dialog open={audioDialogOpen} onClose={closeAudioDialog} fullWidth maxWidth="sm">
        <DialogTitle>Generate GPT Audio</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Text to synthesize"
              value={audioText}
              onChange={(e) => setAudioText(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            <TextField
              label="Pronunciation/style (optional)"
              placeholder="e.g., French Parisian accent, slow learner pace"
              value={audioInstruction}
              onChange={(e) => setAudioInstruction(e.target.value)}
              fullWidth
            />
            <TextField
              label="Voice"
              value={audioVoice}
              onChange={(e) => setAudioVoice(e.target.value)}
              helperText="Examples: alloy, verse, nova, echo"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAudioDialog}>Cancel</Button>
          <Button onClick={submitGenerateAudio} variant="contained" disabled={mediaBusyId === audioDialogCard?.id}>
            {mediaBusyId === audioDialogCard?.id ? "Generating..." : "Generate Audio"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
